import { DEFAULT_BRAND_THEME } from './defaults'

export function normalizeHex(hex) {
  if (typeof hex !== 'string') return null
  const value = hex.trim()
  if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value)) return null
  if (value.length === 4) {
    return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`
  }
  return value.toLowerCase()
}

function hexToRgb(hex) {
  const normalized = normalizeHex(hex)
  if (!normalized) return null
  const raw = normalized.slice(1)
  return {
    r: Number.parseInt(raw.slice(0, 2), 16),
    g: Number.parseInt(raw.slice(2, 4), 16),
    b: Number.parseInt(raw.slice(4, 6), 16),
  }
}

function rgbToHex({ r, g, b }) {
  const clamp = (value) => Math.max(0, Math.min(255, Math.round(value)))
  return `#${[clamp(r), clamp(g), clamp(b)]
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')}`
}

export function normalizeCssColor(value) {
  if (typeof value !== 'string') return null
  const normalizedHex = normalizeHex(value)
  if (normalizedHex) return normalizedHex

  const rgbMatch = value
    .trim()
    .match(
      /^rgba?\(\s*(\d{1,3})\s*[,\s]\s*(\d{1,3})\s*[,\s]\s*(\d{1,3})(?:\s*[,/]\s*[\d.]+\s*)?\)$/i
    )
  if (!rgbMatch) return null

  return rgbToHex({
    r: Number.parseInt(rgbMatch[1], 10),
    g: Number.parseInt(rgbMatch[2], 10),
    b: Number.parseInt(rgbMatch[3], 10),
  })
}

function mixHex(baseHex, targetHex, ratio) {
  const base = hexToRgb(baseHex)
  const target = hexToRgb(targetHex)
  if (!base || !target) {
    return normalizeHex(baseHex) || normalizeHex(targetHex) || '#cccccc'
  }

  return rgbToHex({
    r: base.r + (target.r - base.r) * ratio,
    g: base.g + (target.g - base.g) * ratio,
    b: base.b + (target.b - base.b) * ratio,
  })
}

export function buildBrandPalette(theme = DEFAULT_BRAND_THEME) {
  if (Array.isArray(theme.palette) && theme.palette.length > 0) {
    return theme.palette
      .map((color) => normalizeHex(typeof color === 'string' ? color : color?.hex))
      .filter(Boolean)
  }

  const primary = normalizeHex(theme.primaryColor) || DEFAULT_BRAND_THEME.primaryColor
  const secondary =
    normalizeHex(theme.secondaryColor) || DEFAULT_BRAND_THEME.secondaryColor

  return [
    primary,
    secondary,
    mixHex(primary, '#ffffff', 0.2),
    mixHex(secondary, '#111827', 0.18),
    mixHex(primary, '#111827', 0.12),
    mixHex(secondary, '#ffffff', 0.22),
  ]
}

export function buildBrandColorScaleOption(theme, domain = []) {
  const palette = buildBrandPalette(theme)
  return buildPaletteColorScaleOption(
    palette.map((hex) => ({ hex })),
    domain,
    normalizeHex(theme?.primaryColor) || DEFAULT_BRAND_THEME.primaryColor
  )
}

export function buildPaletteColorScaleOption(
  palette = [],
  domain = [],
  fallbackDefaultColor = DEFAULT_BRAND_THEME.primaryColor
) {
  const normalizedPalette = (palette || [])
    .map((color) =>
      normalizeHex(typeof color === 'string' ? color : color?.hex || color?.range)
    )
    .filter(Boolean)

  const chartSafePalette = normalizedPalette.filter(
    (color) => color !== '#ffffff'
  )

  const colors = chartSafePalette.length
    ? chartSafePalette
    : normalizedPalette.length
      ? normalizedPalette
    : [normalizeHex(fallbackDefaultColor) || DEFAULT_BRAND_THEME.primaryColor]

  return {
    scaleType: 'ordinal',
    interpolator: 'schemeCategory10',
    defaultColor: colors[0],
    defaultOpacity: 1,
    locked: false,
    userScaleValues: domain.map((item, index) => ({
      domain: item,
      range: colors[index % colors.length],
    })),
  }
}
